import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs'
import { map } from 'rxjs/operators'
import { HomeElem } from '../../shared/models/main/home-elem'
import { UserService } from '../user-info/user.service'

export interface ApiResponse<T> {
  results: T[]
  count: number
  detail?: string
}

interface HomeState {
  items: HomeElem[]
  currentPage: {
    items: number
  }
  totalItems: {
    items: number
  }
  loading: {
    items: boolean
  }
}

@Injectable({
  providedIn: 'root',
})
export class HomeStateService {
  private readonly ITEMS_PER_PAGE = 10

  private state: HomeState = {
    items: [],
    currentPage: {
      items: 1,
    },
    totalItems: {
      items: 0,
    },
    loading: {
      items: false,
    },
  }

  private stateSubject = new BehaviorSubject<HomeState>(this.state)

  constructor(private userService: UserService) {}

  // Selectors
  get items$(): Observable<HomeElem[]> {
    return this.stateSubject.pipe(map(state => state.items))
  }

  get totalItems$(): Observable<{ items: number }> {
    return this.stateSubject.pipe(map(state => state.totalItems))
  }

  get itemsLoading$(): Observable<boolean> {
    return this.stateSubject.pipe(map(state => state.loading.items))
  }

  // Actions
  async loadItems(reset: boolean = false): Promise<void> {
    if (reset) {
      this.updateState({
        ...this.state,
        currentPage: { ...this.state.currentPage, items: 1 },
        items: [],
      })
    }

    this.updateState({
      ...this.state,
      loading: { ...this.state.loading, items: true },
    })

    try {
      const response = (await firstValueFrom(
        this.userService.get_user_home_page(this.state.currentPage.items, this.ITEMS_PER_PAGE),
      )) as ApiResponse<HomeElem>

      if (response && response.results) {
        this.updateState({
          ...this.state,
          items: reset ? response.results : [...this.state.items, ...response.results],
          totalItems: { ...this.state.totalItems, items: response.count || 0 },
          loading: { ...this.state.loading, items: false },
        })
      } else {
        throw new Error('Invalid response format from items API')
      }
    } catch (error) {
      this.updateState({
        ...this.state,
        loading: { ...this.state.loading, items: false },
      })
      throw error
    }
  }

  loadMoreItems(): Promise<void> | undefined {
    if (!this.state.loading.items) {
      this.updateState({
        ...this.state,
        currentPage: {
          ...this.state.currentPage,
          items: this.state.currentPage.items + 1,
        },
      })
      return this.loadItems()
    }
    return undefined
  }

  addWord(word: HomeElem): void {
    // Update the state with new word and increment total count
    this.updateState({
      ...this.state,
      items: [word, ...this.state.items],
      totalItems: {
        ...this.state.totalItems,
        items: this.state.totalItems.items + 1,
      },
    })
  }

  addPair(pair: HomeElem): void {
    // Update the state with new pair and increment total count
    this.updateState({
      ...this.state,
      items: [pair, ...this.state.items],
      totalItems: {
        ...this.state.totalItems,
        items: this.state.totalItems.items + 1,
      },
    })
  }

  addDictionary(dictionary: HomeElem): void {
    // Update the state with new dictionary and increment total count
    this.updateState({
      ...this.state,
      items: [dictionary, ...this.state.items],
      totalItems: {
        ...this.state.totalItems,
        items: this.state.totalItems.items + 1,
      },
    })
  }

  removeItem(itemId: number): void {
    // Remove item from state and decrement total count
    const updatedItems = this.state.items.filter(item => item.id !== itemId)
    this.updateState({
      ...this.state,
      items: updatedItems,
      totalItems: {
        ...this.state.totalItems,
        items: updatedItems.length,
      },
    })
  }

  updateItem(item: HomeElem): void {
    // Update the state with modified item
    const updatedItems = this.state.items.map(i => (i.id === item.id ? item : i))
    this.updateState({
      ...this.state,
      items: updatedItems,
    })
  }

  updateTotalCounts(itemCount: number): void {
    this.updateState({
      ...this.state,
      totalItems: {
        items: itemCount,
      },
    })
  }

  private updateState(newState: HomeState): void {
    this.state = newState
    this.stateSubject.next(this.state)
  }
}
